/**
 *  Gifts with Purchase (GWP) — storefront cart reconciler.
 *
 *  Reads the rules emitted by `snippets/gwp-config.liquid` (from the shop
 *  metafield `custom.gwp_rules`) and keeps the cart in sync with them:
 *
 *   - When a rule's trigger matches the current cart, its `auto_add` gift
 *     products are added, tagged with the line-item property `_gwp: <ruleId>`.
 *   - When the trigger no longer matches (or the rule is disabled / outside
 *     its start–end window / off-target),
 *     the tagged gift lines are removed again.
 *   - Gift quantities are enforced: any manual change is reset to the configured
 *     amount. (The drawer/cart UI also hides the qty + remove controls — see
 *     `snippets/cart-drawer-item.liquid` — this is the server-side backstop.)
 *
 *  Only the `auto_add` action is handled here; `popup` notifications are owned
 *  by a separate component.
 */
(function () {
  const GWP_PROPERTY = "_gwp";

  /**
   *  Toast shown when a gift is added — markup lives in
   *  `snippets/gwp-notification.liquid`. Defined up-front so the element always
   *  upgrades, even on pages/stores without any GWP rules.
   */
  if (!customElements.get("gwp-notification")) {
    class GwpNotification extends HTMLElement {
      connectedCallback() {
        this.media = this.querySelector("[data-gwp-media]");
        this.image = this.querySelector("[data-gwp-image]");
        this.heading = this.querySelector("[data-gwp-heading]");
        this.text = this.querySelector("[data-gwp-text]");
        const closeBtn = this.querySelector("[data-gwp-close]");
        if (closeBtn) {
          closeBtn.addEventListener("click", () => this.hide());
        }
      }

      /** @param {{heading?:string, text?:string, image?:string|null}} data */
      show(data) {
        if (this.heading) {
          this.heading.textContent = data.heading || "";
        }
        if (this.text) {
          this.text.textContent = data.text || "";
          this.text.hidden = !data.text;
        }
        if (this.media) {
          if (data.image) {
            this.image.src = data.image;
            this.media.hidden = false;
          } else {
            this.media.hidden = true;
          }
        }

        this.hidden = false;
        void this.offsetWidth; // force reflow so the transition plays
        this.classList.add("gwp-notification--visible");

        clearTimeout(this.hideTimer);
        this.hideTimer = setTimeout(() => this.hide(), 5000);
      }

      hide() {
        clearTimeout(this.hideTimer);
        this.classList.remove("gwp-notification--visible");
        clearTimeout(this.removeTimer);
        this.removeTimer = setTimeout(() => {
          this.hidden = true;
        }, 400);
      }
    }
    customElements.define("gwp-notification", GwpNotification);
  }

  const configEl = document.getElementById("gwp-config");
  if (!configEl) {
    return;
  }

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    console.warn("[gwp] could not parse config", e);
    return;
  }

  const rules = (config.rules || []).filter(
    (rule) => rule && rule.action && rule.action.type === "auto_add",
  );
  if (!rules.length) {
    return;
  }

  const routes = (window.theme && window.theme.routes) || window.Shopify.routes;
  const cartAddUrl = routes.cart_add_url || `${window.Shopify.routes.root}cart/add`;
  const cartChangeUrl = routes.cart_change_url || `${window.Shopify.routes.root}cart/change`;
  const cartUrl = routes.cart_url || `${window.Shopify.routes.root}cart`;

  /** Cart sections to re-render after we mutate the cart (mirrors app.js). */
  const SECTIONS = [
    { id: "Cart", section: "main-cart", selector: ".thb-cart-form" },
    { id: "Cart-Drawer", section: "cart-drawer", selector: ".cart-drawer" },
    { id: "cart-drawer-toggle", section: "cart-bubble", selector: ".thb-item-count" },
  ];

  class GwpManager {
    constructor() {
      this.busy = false;
      this.pending = false;
      this.rulesById = new Map(rules.map((rule) => [rule.id, rule]));

      // React to the theme's cart events. Our own raw fetches below do NOT
      // dispatch these, so there is no reconcile loop.
      ["cart:item-added", "line-item:change:end"].forEach((evt) => {
        document.addEventListener(evt, () => this.schedule());
      });

      this.schedule();
    }

    /** Debounce-ish gate so overlapping cart events collapse into one pass. */
    schedule() {
      if (this.busy) {
        this.pending = true;
        return;
      }
      this.reconcile();
    }

    isGiftLine(item) {
      return item.properties && item.properties[GWP_PROPERTY];
    }

    /** True when the rule may act right now (enabled + within schedule + audience match). */
    ruleActive(rule) {
      if (!rule.enabled) {
        return false;
      }
      if (!this.ruleInSchedule(rule)) {
        return false;
      }
      if (rule.targeting === "logged_in" && !config.customerLoggedIn) {
        return false;
      }
      return true;
    }

    /**
     *  True when now lies within the rule's `startsAt`–`endsAt` window (ISO-8601,
     *  copies of its discount's dates written by the admin app). A missing or
     *  unparsable bound means no limit on that side.
     */
    ruleInSchedule(rule) {
      const now = Date.now();
      const start = rule.startsAt ? Date.parse(rule.startsAt) : NaN;
      const end = rule.endsAt ? Date.parse(rule.endsAt) : NaN;
      if (!Number.isNaN(start) && now < start) {
        return false;
      }
      if (!Number.isNaN(end) && now > end) {
        return false;
      }
      return true;
    }

    triggerMatches(rule, cart) {
      const trigger = rule.trigger || {};
      const eligibleItems = cart.items.filter((item) => !this.isGiftLine(item));

      if (trigger.type === "product") {
        const handles = trigger.triggerProductHandles || [];
        return eligibleItems.some((item) => handles.indexOf(item.handle) !== -1);
      }

      if (trigger.type === "cart_value") {
        const excluded = trigger.excludedProductHandles || [];
        // Thresholds are stored in cents, matching Shopify line prices.
        const subtotal = eligibleItems
          .filter((item) => excluded.indexOf(item.handle) === -1)
          .reduce((sum, item) => sum + item.final_line_price, 0);

        const min = trigger.minThreshold != null ? trigger.minThreshold : 0;
        const upper = trigger.upperThreshold;
        if (subtotal < min) {
          return false;
        }
        if (upper != null && subtotal > upper) {
          return false;
        }
        return true;
      }

      return false;
    }

    /** Key that uniquely identifies a gift line: one rule + one variant. */
    lineKey(ruleId, variantId) {
      return `${ruleId}::${variantId}`;
    }

    /** Build the map of gift lines that SHOULD exist for the given cart. */
    desiredGifts(cart) {
      const desired = new Map();
      rules.forEach((rule) => {
        if (!this.ruleActive(rule) || !this.triggerMatches(rule, cart)) {
          return;
        }
        (rule.action.giftProducts || []).forEach((gift) => {
          const product = (config.products || {})[gift.handle];
          if (!product || !product.variantId) {
            // Unavailable / unresolved gift product — cannot add it.
            return;
          }
          const key = this.lineKey(rule.id, product.variantId);
          desired.set(key, {
            ruleId: rule.id,
            variantId: product.variantId,
            quantity: gift.quantity || 1,
          });
        });
      });
      return desired;
    }

    /** Map current in-cart gift lines by their rule+variant key. */
    currentGifts(cart) {
      const current = new Map();
      cart.items.forEach((item) => {
        if (!this.isGiftLine(item)) {
          return;
        }
        const key = this.lineKey(item.properties[GWP_PROPERTY], item.variant_id);
        current.set(key, { key: item.key, quantity: item.quantity });
      });
      return current;
    }

    async reconcile() {
      this.busy = true;
      let lastMutation = null;
      const addedRuleIds = [];
      try {
        const cart = await fetch(`${cartUrl}.js`).then((r) => r.json());

        const desired = this.desiredGifts(cart);
        const current = this.currentGifts(cart);

        // Remove gift lines that are no longer wanted, or fix wrong quantities.
        for (const [key, line] of current) {
          const want = desired.get(key);
          if (!want) {
            lastMutation = await this.changeLine(line.key, 0);
          } else if (want.quantity !== line.quantity) {
            lastMutation = await this.changeLine(line.key, want.quantity);
          }
        }

        // Add gift lines that are wanted but not present yet.
        for (const [key, want] of desired) {
          if (!current.has(key)) {
            lastMutation = await this.addGift(want);
            addedRuleIds.push(want.ruleId);
          }
        }
      } catch (e) {
        console.warn("[gwp] reconcile failed", e);
      } finally {
        this.busy = false;
      }

      if (lastMutation && lastMutation.sections) {
        this.render(lastMutation);
      }

      this.notify(addedRuleIds);

      if (this.pending) {
        this.pending = false;
        this.schedule();
      }
    }

    /** Show the "gift added" toast for the first newly-added rule that wants one. */
    notify(addedRuleIds) {
      const seen = new Set();
      for (const ruleId of addedRuleIds) {
        if (seen.has(ruleId)) {
          continue;
        }
        seen.add(ruleId);

        const rule = this.rulesById.get(ruleId);
        const notif = rule && rule.action && rule.action.notification;
        if (!notif || !notif.enabled) {
          continue;
        }

        const toast = document.querySelector("gwp-notification");
        if (!toast || typeof toast.show !== "function") {
          return;
        }

        let image = null;
        if (notif.image !== "none") {
          const firstGift = (rule.action.giftProducts || [])[0];
          const product = firstGift ? (config.products || {})[firstGift.handle] : null;
          image = product ? product.image : null;
        }

        toast.show({ heading: notif.headline, text: notif.subline, image });
        return; // one toast at a time (matches the single-toast design)
      }
    }

    addGift(want) {
      const body = {
        items: [
          {
            id: want.variantId,
            quantity: want.quantity,
            properties: { [GWP_PROPERTY]: want.ruleId },
          },
        ],
        sections: SECTIONS.map((s) => s.section).join(","),
        sections_url: window.location.pathname,
      };
      return fetch(`${cartAddUrl}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
      })
        .then((r) => r.json())
        .catch((e) => {
          console.warn("[gwp] add failed", e);
          return null;
        });
    }

    changeLine(key, quantity) {
      const body = {
        id: key,
        quantity,
        sections: SECTIONS.map((s) => s.section).join(","),
        sections_url: window.location.pathname,
      };
      return fetch(`${cartChangeUrl}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
      })
        .then((r) => r.json())
        .catch((e) => {
          console.warn("[gwp] change failed", e);
          return null;
        });
    }

    getSectionInnerHTML(html, selector) {
      const parsed = new DOMParser().parseFromString(html, "text/html").querySelector(selector);
      return parsed ? parsed.innerHTML : null;
    }

    /** Swap the cart sections in place and re-bind the theme's cart JS. */
    render(state) {
      SECTIONS.forEach((section) => {
        const host = document.getElementById(section.id);
        if (!host || !state.sections[section.section]) {
          return;
        }
        const target = host.querySelector(section.selector) || host;
        const html = this.getSectionInnerHTML(state.sections[section.section], section.selector);
        if (html != null) {
          target.innerHTML = html;
        }
      });

      if (document.getElementById("Cart-Drawer") && typeof CartDrawer !== "undefined") {
        new CartDrawer();
      }
      if (document.getElementById("Cart") && typeof Cart !== "undefined") {
        new Cart().renderContents(state);
      }
    }
  }

  const start = () => new GwpManager();
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
